import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Page transition variants
export const pageVariants = {
  initial: { 
    opacity: 0, 
    y: 20,
    filter: 'blur(10px)'
  },
  enter: { 
    opacity: 1, 
    y: 0,
    filter: 'blur(0px)',
    transition: { 
      duration: 0.4, 
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: { 
    opacity: 0, 
    y: -20,
    filter: 'blur(10px)',
    transition: { 
      duration: 0.3 
    }
  }
};

// Slide transitions
export const slideVariants = {
  left: {
    initial: { x: -100, opacity: 0 },
    enter: { x: 0, opacity: 1, transition: { type: 'spring', damping: 25 } },
    exit: { x: 100, opacity: 0 }
  },
  right: {
    initial: { x: 100, opacity: 0 },
    enter: { x: 0, opacity: 1, transition: { type: 'spring', damping: 25 } },
    exit: { x: -100, opacity: 0 }
  },
  up: {
    initial: { y: 100, opacity: 0 },
    enter: { y: 0, opacity: 1, transition: { type: 'spring', damping: 25 } },
    exit: { y: -100, opacity: 0 }
  },
  down: {
    initial: { y: -100, opacity: 0 },
    enter: { y: 0, opacity: 1, transition: { type: 'spring', damping: 25 } },
    exit: { y: 100, opacity: 0 }
  }
};

// Scale transitions
export const scaleVariants = {
  initial: { scale: 0.9, opacity: 0 },
  enter: { 
    scale: 1, 
    opacity: 1,
    transition: { type: 'spring', damping: 20, stiffness: 300 }
  },
  exit: { scale: 0.9, opacity: 0 }
};

// Stagger children animation
export const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1
    }
  }
};

export const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: { type: 'spring', damping: 25 }
  }
};

// Page Transition Wrapper
export const PageTransition = ({ children, className = '' }) => (
  <motion.div
    variants={pageVariants}
    initial="initial"
    animate="enter"
    exit="exit"
    className={className}
  >
    {children}
  </motion.div>
);

// Slide Transition Wrapper
export const SlideTransition = ({ children, direction = 'left', className = '' }) => (
  <motion.div
    variants={slideVariants[direction]}
    initial="initial"
    animate="enter"
    exit="exit"
    className={className}
  >
    {children}
  </motion.div>
);

// Scale Transition Wrapper
export const ScaleTransition = ({ children, className = '' }) => (
  <motion.div
    variants={scaleVariants}
    initial="initial"
    animate="enter"
    exit="exit"
    className={className}
  >
    {children}
  </motion.div>
);

// Stagger List Container
export const StaggerList = ({ children, className = '' }) => (
  <motion.div
    variants={staggerContainer}
    initial="hidden"
    animate="show"
    className={className}
  >
    {children}
  </motion.div>
);

// Stagger List Item
export const StaggerItem = ({ children, className = '' }) => (
  <motion.div variants={staggerItem} className={className}>
    {children}
  </motion.div>
);

// Fade In component
export const FadeIn = ({ 
  children, 
  delay = 0, 
  duration = 0.4, 
  className = '' 
}) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay, duration }}
    className={className}
  >
    {children}
  </motion.div>
);

// Slide Up Fade In
export const SlideUpFadeIn = ({ 
  children, 
  delay = 0, 
  distance = 20,
  className = '' 
}) => (
  <motion.div
    initial={{ opacity: 0, y: distance }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ 
      delay, 
      type: 'spring', 
      damping: 25,
      stiffness: 200
    }}
    className={className}
  >
    {children}
  </motion.div>
);

// Hover Scale
export const HoverScale = ({ 
  children, 
  scale = 1.05, 
  className = '' 
}) => (
  <motion.div
    whileHover={{ scale }}
    whileTap={{ scale: 0.98 }}
    className={className}
  >
    {children}
  </motion.div>
);

// Glow Pulse Animation
export const GlowPulse = ({ 
  children, 
  color = '#00F0FF', 
  className = '' 
}) => (
  <motion.div
    animate={{
      boxShadow: [
        `0 0 0px ${color}00`,
        `0 0 20px ${color}40`,
        `0 0 0px ${color}00`
      ]
    }}
    transition={{ duration: 2, repeat: Infinity }}
    className={className}
  >
    {children}
  </motion.div>
);

// Morphing Background
export const MorphBackground = ({ className = '' }) => (
  <div className={`absolute inset-0 overflow-hidden ${className}`}>
    <motion.div
      className="absolute -top-1/2 -left-1/2 w-full h-full rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(0,240,255,0.1) 0%, transparent 50%)'
      }}
      animate={{
        x: [0, 100, 0],
        y: [0, 50, 0],
        scale: [1, 1.2, 1]
      }}
      transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
    />
    <motion.div
      className="absolute -bottom-1/2 -right-1/2 w-full h-full rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(255,232,0,0.05) 0%, transparent 50%)'
      }}
      animate={{
        x: [0, -100, 0],
        y: [0, -50, 0],
        scale: [1, 1.3, 1]
      }}
      transition={{ duration: 25, repeat: Infinity, ease: 'linear' }}
    />
  </div>
);

// Animated Counter
export const AnimatedCounter = ({ value, duration = 1, className = '' }) => {
  const [displayValue, setDisplayValue] = React.useState(0);
  const animationRef = React.useRef(null);

  React.useEffect(() => {
    const endValue = typeof value === 'number' ? value : parseInt(value) || 0;
    const startValue = displayValue;
    let startTime = null;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      
      setDisplayValue(Math.floor(startValue + (endValue - startValue) * progress));
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return <span className={className}>{displayValue.toLocaleString()}</span>;
};

// Typing Animation
export const TypingText = ({ text, speed = 50, className = '' }) => {
  const [displayText, setDisplayText] = React.useState('');
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    if (index < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText(prev => prev + text[index]);
        setIndex(prev => prev + 1);
      }, speed);
      return () => clearTimeout(timeout);
    }
  }, [index, text, speed]);

  return (
    <span className={className}>
      {displayText}
      <motion.span
        animate={{ opacity: [1, 0] }}
        transition={{ duration: 0.5, repeat: Infinity }}
        className="inline-block w-0.5 h-5 bg-[#00F0FF] ml-0.5"
      />
    </span>
  );
};

export default PageTransition;
